import { DataTableRow } from './DataTableRow'
import { DataTableSkeleton } from './DataTableSkeleton'
import type { ColumnDefinition, DataTableItem, RowAction, SelectionConfig, EditingCell } from './types'

interface DataTableBodyProps<T extends DataTableItem> {
  data: T[]
  columns: ColumnDefinition<T>[]
  visibleColumns: string[]
  isLoading?: boolean
  selection?: SelectionConfig<T>
  rowActions?: RowAction<T>[]
  isScrolledX?: boolean
  skeletonRowCount?: number
  editingCell?: EditingCell | null
  onStartEdit?: (cell: EditingCell) => void
  onSaveEdit?: (rowId: string | number, columnId: string, value: string) => void
  onCancelEdit?: () => void
  showPlaceholderWhenEmpty?: boolean
  disableRowHover?: boolean
  hasSpacerColumn?: boolean
}

export function DataTableBody<T extends DataTableItem>({
  data,
  columns,
  visibleColumns,
  isLoading = false,
  selection,
  rowActions,
  isScrolledX = false,
  skeletonRowCount = 20,
  editingCell,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  showPlaceholderWhenEmpty = false,
  disableRowHover = false,
  hasSpacerColumn = false
}: DataTableBodyProps<T>) {
  const hasActions = rowActions && rowActions.length > 0

  if (isLoading) {
    return (
      <tbody>
        <DataTableSkeleton
          columns={columns}
          visibleColumns={visibleColumns}
          rowCount={skeletonRowCount}
          isScrolledX={isScrolledX}
          hasActions={hasActions}
          hasSpacerColumn={hasSpacerColumn}
        />
      </tbody>
    )
  }

  if (data.length === 0 && showPlaceholderWhenEmpty) {
    return (
      <tbody>
        <DataTableSkeleton
          columns={columns}
          visibleColumns={visibleColumns}
          rowCount={skeletonRowCount}
          isScrolledX={isScrolledX}
          hasActions={hasActions}
          hasSpacerColumn={hasSpacerColumn}
          noAnimation
        />
      </tbody>
    )
  }

  return (
    <tbody>
      {data.map((row, index) => {
        const isSelected = selection ? selection.selectedIds.has(row.id) : false
        const nextRow = data[index + 1]
        const isNextSelected = nextRow && selection ? selection.selectedIds.has(nextRow.id) : false

        return (
          <DataTableRow
            key={row.id}
            row={row}
            columns={columns}
            visibleColumns={visibleColumns}
            isSelected={isSelected}
            isNextSelected={isNextSelected}
            selection={selection}
            rowActions={rowActions}
            isScrolledX={isScrolledX}
            editingCell={editingCell}
            onStartEdit={onStartEdit}
            onSaveEdit={onSaveEdit}
            onCancelEdit={onCancelEdit}
            disableRowHover={disableRowHover}
            hasSpacerColumn={hasSpacerColumn}
          />
        )
      })}
    </tbody>
  )
}
